import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Checkbox,
  Tooltip,
  Stack,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  CircularProgress
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { get_brand_library, get_folders } from '../../../store/job/jobSlice';
import { get_company_process } from '../../../store/user/userSlice';
import PdfThumbnail from './PdfThumbnail';

const IMAGE_REGEX = /\.(jpeg|jpg|gif|png|webp)$/i;
const PDF_REGEX = /\.pdf$/i;

const BrandLibraryModal = ({
  open,
  onClose,
  onSelect,
  initialSelected = [],
  isPersonal = false
}) => {
  const dispatch = useDispatch();
  const { branded_library } = useSelector(state => state.job);
  const { selected_process_tab, company_process, selected_company } = useSelector(state => state.user);

  const [searchTerm, setSearchTerm] = useState('');
  // Store full objects, not just paths – so selections survive folder navigation
  const [selectedItems, setSelectedItems] = useState([]);
  const [brokenImages, setBrokenImages] = useState({});
  const [isPersonalTab, setIsPersonalTab] = useState(isPersonal);

  // Folder states
  const { folders, folderLoading } = useSelector(state => state.job);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Home" }]);


  const initializedRef = useRef(false);

  /* ---------------- helpers ---------------- */

  const normalizePath = (path = '') =>
    decodeURIComponent(path).split('?')[0];

  const getFileName = (url) =>
    url?.split('?')[0].split('/').pop() || 'File';

  const getFullUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return encodeURI(path);

    return encodeURI(
      `https://sewrobj.us-east-1.linodeobjects.com/${path.replace(/^\/+/, '')}`
    );
  };

  const isImage = (url) => IMAGE_REGEX.test(url);
  const isPdf = (url) => PDF_REGEX.test(url);

  const latestProps = useRef({ selected_process_tab, selected_company });
  useEffect(() => {
    latestProps.current = { selected_process_tab, selected_company };
  });

  useEffect(() => {
    if (!open) return;

    const { selected_company: sCompany, selected_process_tab: sProcess } = latestProps.current;

    if (sCompany && (!company_process || company_process.length === 0)) {
      dispatch(get_company_process(sCompany));
    }

    dispatch(get_brand_library({
      process_id: sProcess || undefined,
      is_personal: isPersonalTab ? true : false,
      folder_id: currentFolderId,
      search: searchTerm.trim() || undefined
    }));

    dispatch(get_folders({
      parent_id: currentFolderId,
      is_personal: isPersonalTab ? true : false,
      search: searchTerm.trim() || undefined
    }));
  }, [open, isPersonalTab, dispatch, currentFolderId]);


  useEffect(() => {
    setIsPersonalTab(isPersonal);
  }, [isPersonal, open]);

  /* ---------------- initialize selection ONLY ON OPEN ---------------- */

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }

    if (!initializedRef.current) {
      // initialSelected is an array of file-path strings coming from the parent;
      // seed selectedItems with minimal objects so confirm works immediately.
      setSelectedItems(
        initialSelected
          .filter(Boolean)
          .map(p => ({ file_path: normalizePath(p) }))
      );
      initializedRef.current = true;
    }
  }, [open, initialSelected]);

  /* ---------------- selection logic ---------------- */

  const handleToggleSelect = (item) => {
    const normalizedPath = normalizePath(item.file_path);

    setSelectedItems(prev => {
      const alreadySelected = prev.some(
        s => normalizePath(s.file_path) === normalizedPath
      );
      if (alreadySelected) {
        // Deselect: remove the matching object
        return prev.filter(s => normalizePath(s.file_path) !== normalizedPath);
      }
      // Select: add the full item object so we keep it across folder changes
      return [...prev, item];
    });
  };

  const handleConfirmSelection = () => {
    // selectedItems already holds full objects – pass them directly.
    // No dependency on branded_library so cross-folder picks are preserved.
    onSelect(selectedItems);
    onClose();
  };
  /* ---------------- search ---------------- */

  const handleSearch = () => {
    dispatch(get_brand_library({
      process_id: selected_process_tab || undefined,
      search: searchTerm,
      is_personal: isPersonalTab ? true : false,
      folder_id: currentFolderId
    }));
    dispatch(get_folders({
      parent_id: currentFolderId,
      is_personal: isPersonalTab ? true : false,
      search: searchTerm
    }));
  };

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (id, index) => {
    setCurrentFolderId(id);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  };


  /* ---------------- render thumbnail ---------------- */

  const renderThumbnail = (item) => {
    const url = getFullUrl(item.file_path);

    if (!url || brokenImages[url]) {
      return <DescriptionIcon sx={{ fontSize: 60, color: '#bdbdbd' }} />;
    }

    if (isImage(url)) {
      return (
        <img
          src={url}
          alt={getFileName(url)}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          onError={() => setBrokenImages(p => ({ ...p, [url]: true }))}
        />
      );
    }

    if (isPdf(url)) {
      return <PdfThumbnail url={url} />;
    }

    return <DescriptionIcon sx={{ fontSize: 60, color: '#bdbdbd' }} />;
  };

  /* ---------------- UI ---------------- */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Library Selection
        {selectedItems.length > 0 && (
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 1,
              py: 0.25,
              bgcolor: '#ff6100',
              color: '#fff',
              borderRadius: '12px',
              fontSize: '0.78rem',
              fontWeight: 700,
              lineHeight: 1.6
            }}
          >
            {selectedItems.length} selected
          </Box>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={isPersonalTab ? 1 : 0}
            onChange={(e, val) => {
              setIsPersonalTab(val === 1);
              setCurrentFolderId(null);
              setBreadcrumbs([{ id: null, name: "Home" }]);
            }}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' },
              '& .Mui-selected': { color: '#ff6100' },
              '& .MuiTabs-indicator': { backgroundColor: '#ff6100' }
            }}
          >
            <Tab label="Brand Library" />
            <Tab label="My Library" />
          </Tabs>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: 16 }} />}>
            {breadcrumbs.map((bc, index) => (
              <Link
                key={bc.id || 'root'}
                underline="hover"
                color={index === breadcrumbs.length - 1 ? "text.primary" : "inherit"}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: '0.85rem' }}
                onClick={() => handleBreadcrumbClick(bc.id, index)}
              >
                {index === 0 && <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />}
                {bc.name}
              </Link>
            ))}
          </Breadcrumbs>
        </Box>


        <TextField
          fullWidth
          placeholder="Search files..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ mb: 3 }}
        />

        <Grid container spacing={2}>
          {folderLoading ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={30} sx={{ color: '#ff6100' }} />
            </Grid>
          ) : (
            <>
              {/* Folders */}
              {folders?.map(folder => (
                <Grid item xs={6} sm={4} md={3} key={`folder-${folder.id}`}>
                  <Box
                    onClick={() => handleFolderClick(folder)}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '12px',
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#ff6100',
                        bgcolor: 'rgba(255, 97, 0, 0.05)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.05)'
                      }
                    }}
                  >
                    <FolderIcon sx={{ color: '#ffc107', mr: 1.5, fontSize: 32 }} />
                    <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                      {folder.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}

              {/* Files */}
              {branded_library?.length ? branded_library.map(item => {

                const isSelected = selectedItems.some(
                  s => normalizePath(s.file_path) === normalizePath(item.file_path)
                );
                return (
                  <Grid item xs={6} sm={4} md={3} key={item.id}>
                    <Box sx={{
                      border: '1px solid',
                      borderColor: isSelected ? '#ff6100' : '#e0e0e0',
                      borderRadius: '12px',
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden',
                      bgcolor: '#fff',
                      transition: 'all 0.2s',
                      boxShadow: isSelected ? '0 0 0 2px rgba(255, 97, 0, 0.2)' : 'none',
                      '&:hover': {
                        boxShadow: isSelected ? '0 4px 12px rgba(255, 97, 0, 0.3)' : '0 4px 12px rgba(0,0,0,0.08)',
                        borderColor: isSelected ? '#ff6100' : '#bdbdbd'
                      }
                    }}>
                      <Box
                        sx={{
                          height: 150,
                          maxHeight: 150,
                          overflow: 'hidden',
                          bgcolor: '#f9f9f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          p: 1.5,
                          position: 'relative',
                        }}
                        onClick={() => window.open(getFullUrl(item.file_path), '_blank')}
                      >
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {renderThumbnail(item)}
                        </Box>
                      </Box>

                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        borderTop: '1px solid #eee',
                        bgcolor: isSelected ? 'rgba(255, 97, 0, 0.05)' : '#fff'
                      }}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(item)}
                          sx={{ p: 0.5, mr: 0.5, color: '#ff6100', '&.Mui-checked': { color: '#ff6100' } }}
                        />

                        <Tooltip title={item.name || getFileName(item.file_path)}>
                          <Typography
                            noWrap
                            sx={{
                              fontSize: '0.85rem',
                              fontWeight: isSelected ? 600 : 500,
                              cursor: 'pointer',
                              color: isSelected ? '#ff6100' : '#333'
                            }}
                            onClick={() => handleToggleSelect(item)}
                          >
                            {item.name || getFileName(item.file_path)}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                );
              }) : (!folders?.length && (
                <Grid item xs={12}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No folders or files found here
                  </Typography>
                </Grid>
              ))}
            </>
          )}
        </Grid>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={selectedItems.length === 0}
          onClick={handleConfirmSelection}
          sx={{ bgcolor: '#ff6100', '&:hover': { bgcolor: '#e65700' } }}
        >
          Add Selected ({selectedItems.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandLibraryModal;
